import { computed, getCurrentInstance, ref } from "vue";
import ProcessManager from "@/components/Workflows/processManager";
import { Error } from "@/core/plugins/swal";
import { useStore } from "@/core/services/store";
import { useFeatures } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useFeatures";
import {
  addEventToLoadingQueue,
  removeEventFromLoadingQueue
} from "@/composables/useLoadingQueue";
import { salesOrderFeatures } from "@/components/ExternalApps/SalesChannelManagementApp/utility/constants";
import { useUtility } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useUtility";
import { useIntegration } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useIntegration";
import { transformParameters } from "@/components/ExternalApps/SalesChannelManagementApp/utility/reporting";
import { labelData } from "@/components/ExternalApps/SalesChannelManagementApp/utility/labelData";

export const useTesting = (formComponent, workflowId) => {
  const formData = ref();
  const showModal = ref(false);
  const { selectedFeature } = useFeatures();
  const { selectedIntegration, selectedIntegrationName } = useIntegration();
  const { xentralProjectHeader } = useUtility();
  const store = useStore();
  const BATCH_WORKFLOW_ID = computed(() => {
    // We currently don't have the possibility to batch test payment transactions
    if (selectedFeature.value === "paymentTransactions") return;

    return store.getters.testBatchWorkflowId(selectedFeature.value);
  });
  const computedWorkflowId = computed(
    () => workflowId ?? BATCH_WORKFLOW_ID.value
  );
  const orderKey = computed(() =>
    store.getters.selectedApp.name === "woocommerce"
      ? "orderNumbers"
      : "orderIds"
  );
  const orderIdentifierKey = computed(() =>
    store.getters.selectedApp.name === "woocommerce" ? "orderNumber" : "orderId"
  );
  const i18n = getCurrentInstance().proxy.$i18n;
  const formConfig = ref({
    snippetPrefix: "salesChannelManagementApp",
    labelColClass: "col-5",
    fieldColClass: "col-7",
    fieldColClassXl: "col-7",
    ...labelData.value
  });
  const numberKeyPerApp = {
    woocommerce: {
      salesOrder: "orderNumber",
      tracking: "orderNumber"
    },
    shopify: {
      salesOrder: "orderNumberShopify",
      tracking: "orderNumberShopify"
    },
    tradebyte: {
      salesOrder: "orderIdTradebyte",
      tracking: "orderIdTradebyte"
    }
  };
  const numberKey = {
    prices: "productNumber",
    product: "productNumber",
    salesOrder: "orderNumber",
    stock: "productNumber",
    tracking: "orderNumber",
    receipt: "orderNumber",
    paymentTransactions: "orderNumber"
  };
  const currentNumberKey = computed(() => numberKey[selectedFeature.value]);
  const isAmazonReceipt = computed(
    () =>
      store.getters.selectedApp.name === "amazon" &&
      selectedFeature.value === "receipt"
  );
  const form = computed(() => {
    let formConfig = [];

    let label =
      numberKeyPerApp?.[store.getters.appType]?.[selectedFeature.value] ??
      currentNumberKey.value;

    let formField = {
      name: "number",
      type: "text",
      label: `test.${label}`,
      disableReturnType: true,
      validations: {
        required: true
      }
    };

    if (i18n.te(`salesChannelManagementApp.journal.${label}ImportInfoText`)) {
      formField.infoText = i18n.t(
        `salesChannelManagementApp.journal.${label}ImportInfoText`
      );
    }

    if (isAmazonReceipt.value) {
      formField.infoText = i18n.t(
        "salesChannelManagementApp.journal.orderNumberReceiptImportInfoText"
      );
    }

    if (
      i18n.te(`salesChannelManagementApp.journal.${label}ImportPlaceHolder`)
    ) {
      formField.placeholder = i18n.t(
        `salesChannelManagementApp.journal.${label}ImportPlaceHolder`
      );
    }

    formConfig.push(formField);

    if (
      store.getters.selectedApp.name === "amazon" &&
      selectedFeature.value === "salesOrder"
    ) {
      formConfig.push({
        name: "forceImport",
        type: "checkbox",
        default: false,
        label: i18n.t(
          "salesChannelManagementApp.journal.forceImportLabel"
        ),
        infoText: i18n.t(
          "salesChannelManagementApp.journal.forceImportInfoText"
        )
      });
      formConfig.push({
        name: "forceDisablingAutoShipping",
        type: "checkbox",
        default: false,
        label: i18n.t(
          "salesChannelManagementApp.journal.forceDisablingAutoShippingLabel"
        ),
        infoText: i18n.t(
          "salesChannelManagementApp.journal.forceDisablingAutoShippingInfoText"
        )
      });
    }

    if (isAmazonReceipt.value) {
      formConfig.push({
        name: "receiptType",
        type: "select",
        clearable: false,
        default: "invoice",
        label: i18n.t("salesChannelManagementApp.reporting.receiptType"),
        options: [
          {
            label: i18n.t(
              "salesChannelManagementApp.reporting.receiptTypes.invoice"
            ),
            value: "invoice"
          },
          {
            label: i18n.t(
              "salesChannelManagementApp.reporting.receiptTypes.creditNote"
            ),
            value: "creditNote"
          }
        ],
        validations: {
          required: true
        }
      });
    }

    return formConfig;
  });

  async function testWorkflow() {
    if (formComponent.value.validate() !== true) {
      Error(i18n.t("salesChannelManagementApp.test.requiredFields"));
      return;
    }

    let params = {};

    if (formData.value?.number?.includes(",")) {
      params["orderNumbers"] = formData.value?.number
        .split(",")
        ?.map(n => n.trim())
        ?.filter(v => v.length > 0);
    } else {
      params["orderNumbers"] = [formData.value?.number];
    }

    if (selectedFeature.value === "prices") {
      params["addPricesToProduct"] = true;
    }

    if (formData.value?.forceImport) {
      params["forceImport"] = true;
    }

    if (formData.value?.forceDisablingAutoShipping) {
      params["forceDisablingAutoShipping"] = true;
    }

    if (isAmazonReceipt.value && formData.value?.receiptType) {
      params["receiptType"] = formData.value.receiptType;
    }

    showModal.value = false;
    await executeBatchWorkflow(params);
  }

  async function requeueJob(entry) {
    const orderIdentifier = entry?.item?.[orderIdentifierKey.value];

    if (!orderIdentifier) return;

    const params = {
      [orderKey.value]: [orderIdentifier]
    };

    await executeBatchWorkflow(params);
  }

  async function requeueJobs(
    allRowsSelected,
    selectedRows,
    parameters = [],
    isFiltered = false
  ) {
    let params = {};

    if (!allRowsSelected && selectedRows?.length > 0) {
      params[orderKey.value] = selectedRows;
    }

    if (allRowsSelected) {
      const paramsToTransform = parameters.filter(
        item => item.name !== "channelSpecificName"
      );
      params.parameters = transformParameters(paramsToTransform);
    }

    params.isFiltered = isFiltered;

    await executeBatchWorkflow(params);
  }

  async function executeBatchWorkflow(params) {
    addEventToLoadingQueue({
      key: "execute-batch-sync",
      group: "execute-batch-sync"
    });
    let parameters = {
      ...params,
      channelSpecificName: selectedIntegrationName.value,
      test: true
    };

    // Amazon receipt exports run in the project of the selected integration
    let headers = {};

    if (isAmazonReceipt.value) {
      headers = { "x-project-id": selectedIntegration.value.id };
    } else if (
      !salesOrderFeatures.includes(selectedFeature.value) &&
      store.getters.useXentralProjectForSync
    ) {
      headers = xentralProjectHeader.value;
    }

    try {
      const response = await ProcessManager.run(
        computedWorkflowId.value,
        {
          params: parameters
        },
        headers
      );

      if (response?.data?.status === "process.error") {
        Error(response.data.content_identifier);
        removeEventFromLoadingQueue({
          key: "execute-batch-sync",
          group: "execute-batch-sync"
        });
        return;
      }

      removeEventFromLoadingQueue({
        key: "execute-batch-sync",
        group: "execute-batch-sync",
        type: "success",
        prefix: "salesChannelManagementApp",
        name: `reporting.executed.${selectedFeature.value}`
      });
    } catch (error) {
      Error(error);
    } finally {
      removeEventFromLoadingQueue({
        key: "execute-batch-sync",
        group: "execute-batch-sync"
      });
    }
  }

  return {
    showModal,
    form,
    formConfig,
    formData,
    orderIdentifierKey,
    BATCH_WORKFLOW_ID,
    testWorkflow,
    requeueJob,
    requeueJobs
  };
};
